<template>
  <aside
    id="componentGoToPosition"
    v-trap-focus
    :aria-label="t('goToPosition.componentTitle')"
    class="go-to-position absolute h-auto justify-center items-center noselect"
    @keydown.esc="close()"
  >
    <h2 class="w-full text-center pb-3 pt-3 text-lg">
      {{ t("goToPosition.name") }}
    </h2>
    <div class="flex">
      <input
        v-model="position"
        type="number"
        class="go-to-position__box"
        min="1"
        :max="navigator.positions.length"
        @keypress.enter="go"
      />
      <button
        :aria-label="t('goToPosition.searchButton')"
        :title="t('goToPosition.searchButton')"
        class="square-icon-button"
        @click="go"
      >
        <span class="material-icons" aria-hidden="true"> my_location </span>
      </button>
    </div>
    <span
      id="anchorFocusMoreOptionsComponent"
      tabindex="0"
      aria-hidden="true"
    ></span>
  </aside>
</template>

<script setup lang="ts">
import { inject, Ref, ref, watchEffect } from "vue";
import { useI18n } from "vue-i18n";

import { clamp } from "../../../modules/shared-utils/math";
import { trapFocus } from "@/helpers/trapFocus";

import { useNavigatorWithHistory } from "../navigator";
import { useUILayers } from "../UILayers";
import MediaPlayer from "@/services/media/player";
import { getDebugMode } from "@/helpers/helperFunctions";

const { t } = useI18n();
const { close } = useUILayers();
const navigator = useNavigatorWithHistory();
const mediaPlayer = inject("mediaPlayer") as Ref<MediaPlayer>;

const position = ref<number>(1);

const vTrapFocus = {
  mounted: () => {
    document.addEventListener("keydown", keyBoardEvent);
    const anchorFocus = document.getElementById(
      "anchorFocusMoreOptionsComponent"
    );
    anchorFocus?.focus();
  },
  beforeUnmount: () => {
    document.removeEventListener("keydown", keyBoardEvent);
  },
};

function keyBoardEvent(event: KeyboardEvent) {
  const isTabPressed = event.key === "Tab";
  const modal = document.getElementById("componentGoToPosition");

  if (event.key === "ArrowLeft" || event.key === "ArrowRight")
    event.stopImmediatePropagation();
  if (!isTabPressed || !modal) return;

  trapFocus(modal, event);
}

async function go(): Promise<void> {
  if (mediaPlayer.value) {
    mediaPlayer.value.stop();
  }
  await navigator.go(position.value);
  close();
}

watchEffect(() => {
  if (getDebugMode())
    console.log("watchEffect GoToPosition", navigator.positions);
  const positionsArray = navigator.positions;
  position.value = clamp(position.value, 1, positionsArray.length);
});
</script>

<style lang="scss" scoped>
@use "@/assets/styles/reader.scss" as *;
.go-to-position {
  z-index: 3;
  right: $spacingM;
  top: $barHeight;
  padding: 0 0 $spacingM 0;
  color: var(--DM__theme-font-color);
  background-color: var(--DM__theme-fill-color);
  box-shadow: 0 4px 23px rgb(0 0 0 / 9%);
  border: 3px solid rgba(158, 158, 158, 0.2);
  &__box {
    min-width: 160px;
    background-color: transparent;
    padding: $spacingS;
    border: 1px solid rgba(158, 158, 158, 0.2);
    margin: 0 $spacingS 0 $spacingM;
    @include hover();
    &:focus {
      border-color: var(--DM__theme-hover-color);
    }
  }
}
</style>
